import {action, computed, observable, makeObservable} from 'mobx';

import {FormsStore} from './FormsStore';
import {SearchesStore} from '../../Searches/stores/SearchesStore';
import {Column} from '../serializers/column';
import {hiddenFieldTypes} from '../../../constants/FieldConsts';
import {isPainted} from '../../../_shared/GWTBridge/invoke/netForm';
import {getIsNewGrid} from '../../../_shared/GWTBridge/invoke/common';

import {getIsQuickSearch} from '../../QuickSearch/actions/quickSearch';
import {QuickSearchStore} from '../../QuickSearch/store/QuickSearchStore';
import {FormTable} from './FormTable';
import {ReconProperties} from './ReconProperties';
import {ViewType} from '../consts/viewTypes';
import {statusForms} from '../consts/statusForms';
import {EntityType} from '../../../constants/EntityTypes';
import {MacroData} from '../types';
import {FormViewEnum} from '../../../stores/Form/Form';
import {SerializedJson} from '../serializers/form';
import {HelpBook} from '../../../stores/Form/HelpBook';

export type FormFieldData = {
    color: string;
    sameValue: string;
    value: string;
};

export type CurrentCell = {
    row: number;
    gridColumn: number;
    formColumn: number;
    columnName: string;
    value: string;
    headerTitle: string;
};

export type Action = {
    title: string;
    type: EntityType;
    ename: string;
    exec?: string;
};

export type FormLabel = {
    label: string;
    value: string;
};

export type FormLabels = {
    labels: FormLabel[];
    selected: FormLabel;
    label: string;
    clean: string;
    lines: string;
    mayprint: string;
    print: string;
};

export type Row = {[field: string]: string};
export type FormRowData = Map<string, FormFieldData>;
export type FormTableData = Map<number, FormRowData>;
export type ResultTables = Map<string, FormTableData>;
export type Actions = Array<Action>;

export class Form {
    @observable rows: Array<Row> = [];
    @observable _currentCell: CurrentCell;
    @observable reconProperties: ReconProperties;
    @observable queryMode: boolean;
    @observable currentColSum: number;
    @observable showMoreInfo: boolean;
    @observable formLabels: FormLabels;
    @observable macros: MacroData[];
    @observable helpBook = new HelpBook();

    store: FormsStore;
    formJson: SerializedJson;
    searches: SearchesStore;
    table: string;
    name: string;
    title: string;
    session: string;
    isHTML: boolean;
    maySum: boolean;
    self;
    isQuickSearch: boolean;
    quickSearchStore?: QuickSearchStore;
    directActivations: Actions;
    formType: ViewType;
    _formTable: FormTable;
    _isRecon: boolean;
    formData: FormTableData;
    isActive: boolean = false;
    subforms: Map<string, Form> = new Map();
    parentForm: Form;
    statuses: Map<string, string>;
    maydesignform: boolean;
    maxRecursiveMacro: number;
    shownFeaturePopovers: string[];
    currentFieldMoreInfo: null | Array<any> = null;
    formCompanyName: string = null;

    constructor(
        store: FormsStore,
        form,
        constants,
        resultTables: FormTableData,
        json,
        activations,
        defaultSearch,
        session: string,
        parentFormName: string,
        statusesMap: Map<string, string>,
        macros: MacroData[],
        formCompanyName: string = null
    ) {
        makeObservable(this);
        this.store = store;
        this.formJson = json;
        this.searches = new SearchesStore(this, json, defaultSearch);
        this.directActivations = activations;
        this.name = form.name;
        this.table = form.table;
        this.self = form.self;
        this.title = json.title;
        this.maySum = constants.maySum;
        this.maydesignform = constants.maydesignform;
        this.maxRecursiveMacro = constants.maxRecursiveMacro;
        this.shownFeaturePopovers = [];

        this.isQuickSearch = getIsQuickSearch(json);
        this.quickSearchStore = this.isQuickSearch
            ? new QuickSearchStore(form.name, json)
            : undefined;

        this.setFormType(constants);
        this.setSession(session);
        this.setFormData(resultTables);
        this.initCurrentCell();
        this.setFormTable(store, form);
        this.setParentForm(parentFormName);
        this.setIsHTML(json);
        this.setMacros(macros);

        this.queryMode = false;
        this.statuses = statusesMap;
        this.formCompanyName = formCompanyName;
    }

    @action setIsHTML({ishtml}) {
        this.isHTML = ishtml === '1';
    }

    @action setFormLabels(formLabels: FormLabels) {
        this.formLabels = formLabels;
    }

    @action getFormLabels(): FormLabels {
        return this.formLabels;
    }

    @action isStatusForm(): boolean {
        return statusForms.includes(this.name);
    }

    @action.bound toggleSearchMode(rows) {
        this.queryMode = true;
        this.updateRows(rows);
    }

    setParentForm(parentName) {
        if (Number(this.table) > 1 && parentName) {
            this.parentForm = this.store.getFormFromSession(this.session, parentName);
            this.parentForm.subforms.set(this.name, this);
        }
    }

    setIsActive(isActive) {
        this.isActive = isActive;
    }

    setCurrentCell(rowNumber: number, columnNumber: number, formColumn: number, column) {
        let field = '';
        let headerName = '';
        let value = '';

        if (column) {
            field = column.colDef.field;
            headerName = column.colDef.headerName;
        }
        if (this.rows?.length && this.rows?.length >= rowNumber) {
            value = this.rows?.[rowNumber]?.[field] || '';
        }

        this._currentCell = {
            row: rowNumber,
            formColumn: formColumn,
            gridColumn: columnNumber,
            columnName: field,
            value: value,
            headerTitle: headerName
        };
    }

    @action updateRows(tableData: Array<Row>) {
        this.rows = tableData;
    }

    initCurrentCell() {
        this._currentCell = {
            row: 0,
            gridColumn: 0,
            formColumn: 0,
            columnName: '',
            value: '',
            headerTitle: ''
        };
    }

    @action postSearchReset() {
        this._currentCell.row = 0;

        this.queryMode = false;
    }

    setFormTable(store, data) {
        this._formTable = new FormTable(store, data);
    }

    setFormData(formData: FormTableData) {
        this.formData = formData;
    }

    setSession(session: string) {
        this.session = session;
    }

    setFormType({isRecon}) {
        this._isRecon = isRecon;
        if (isRecon && this.table === '1' && getIsNewGrid()) {
            this.formType = ViewType.RECON;
        } else {
            this.formType = ViewType.GENERAL;
        }
    }

    setMacros(macros: MacroData[]) {
        this.macros = macros.slice().sort((a, b) => a.origtitle.localeCompare(b.origtitle));
    }

    addMacro(macro: MacroData) {
        const sameNameMacroIndex = this.macros?.findIndex(item => item.macro === macro.macro);
        if (sameNameMacroIndex === -1) {
            this.macros.push(macro);
            this.macros = this.macros
                .slice()
                .sort((a, b) => a.origtitle.localeCompare(b.origtitle));
        } else {
            this.macros[sameNameMacroIndex] = macro;
        }
    }

    removeMacro(macroID: string) {
        const macros = this.macros.filter(macro => macro.macro !== macroID);
        this.setMacros(macros);
    }

    updateMacroKey(macroID: string, key: string) {
        const macroIndex = this.macros?.findIndex(item => item.macro === macroID);
        const macroSameKeyIndex = this.macros?.findIndex(item => item.key === key);
        if (macroSameKeyIndex !== -1) {
            this.macros[macroSameKeyIndex].key = '';
        }
        this.macros[macroIndex].key = key;
    }

    setShownFeaturePopovers(featureID) {
        this.shownFeaturePopovers.push(featureID);
    }

    isShowFeaturePopover(featureID) {
        return !this.shownFeaturePopovers.includes(featureID);
    }

    /**
     * TODO: fix me
     * isRecon is set to false when we open a recon subform
     */

    @computed get isRecon(): boolean {
        return this._isRecon;
    }

    @computed get currentCell(): CurrentCell {
        return this._currentCell;
    }

    @computed get currentRow(): number {
        return this.currentCell.row;
    }

    @computed get hasLabels(): boolean {
        return !!this?.formLabels?.labels.length;
    }

    @computed get showPrintLabels(): boolean {
        return !this.queryMode && !this.isHTML && !this.isRecon;
    }

    get formTable() {
        return this._formTable as FormTable;
    }

    get formState() {
        const {row, columnName} = this.currentCell;
        const {formColumn} = this.currentCell;
        const value = (this.rows.length && this.rows[row]?.[columnName]) || '';

        return {
            currentFieldNum: formColumn,
            currentLine: row + 1,
            currentFieldValue: value,
            isActive: this.queryMode ? '-1' : this.isActive ? '1' : '0',
            currentTab: 0,
            isPainted: this.isPainted
        };
    }

    get isPainted() {
        return isPainted(this.table);
    }

    get view(): FormViewEnum {
        return this.isPainted ? FormViewEnum.form : FormViewEnum.table;
    }

    get columnsMap() {
        if (this.isPainted) {
            return this.formTable.paintedColumnsMap;
        } else {
            return this.formTable.tableViewColumnsmap;
        }
    }

    get tableViewColumns() {
        return this.formTable.tableViewColumnsmap;
    }

    get zoomField() {
        if (this.isPainted) {
            return this.formTable.paintedZoomField;
        } else {
            return this.formTable.tableViewZoomField;
        }
    }

    get columns(): Array<Column> {
        return Array.from(this.columnsMap.values());
    }

    get visibleColumns() {
        return Array.from(this.columns).filter((col: any) => col.hidden === hiddenFieldTypes.SHOWN);
    }

    get ename() {
        return this.name;
    }

    getColumnByNumber(fieldNum: string) {
        return this.columnsMap.get(fieldNum);
    }

    getColumnByName(fieldName: string) {
        for (const value of this.columnsMap?.values()) {
            if (value.name === fieldName) {
                return value;
            }
        }
        return null;
    }

    @action openFieldMoreInfo() {
        this.showMoreInfo = true;
    }

    @action closeFieldMoreInfo() {
        this.showMoreInfo = false;
        this.setCurrentFieldMoreInfo(null);
    }

    setCurrentFieldMoreInfo(data: Array<any> | null) {
        this.currentFieldMoreInfo = data;
    }
}
